/**
 * View-time live-infra state (DECISIONS #47): which environment the map is
 * showing, persisted PER REPO (`prod` default) — plus the one-time
 * "Live from Render" callout flag set on a successful connect and cleared
 * on the first interaction after it renders.
 *
 * The env choice lives HERE, never on the source: every environment is
 * always read (one poll spans the workspace); badges, deploy alarms, slot
 * math and future queue env-chips all follow this selection by reading the
 * overlay's per-env bucket (`serviceLiveForEnv`).
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { type ViewEnvironment } from './types';

export const DEFAULT_VIEW_ENVIRONMENT: ViewEnvironment = 'prod';

interface LiveViewState {
  /** repoId → selected view environment (absent = prod) */
  envByRepo: Record<string, ViewEnvironment>;
  /** repoId → the post-connect callout is waiting for its map moment */
  calloutPendingByRepo: Record<string, boolean>;
  /** repoId → LIVE mode on (flow Play toggle, DECISIONS #48f/#48g): the
   * overlay poll runs at 2s with ?mode=live. Session-only — never persisted
   * (a reload lands back in the ordinary cadence; polling stays bounded). */
  liveModeByRepo: Record<string, boolean>;
  setViewEnv: (repoId: string, env: ViewEnvironment) => void;
  markCalloutPending: (repoId: string) => void;
  dismissCallout: (repoId: string) => void;
  setLiveMode: (repoId: string, on: boolean) => void;
}

export const useLiveViewStore = create<LiveViewState>()(
  persist(
    (set) => ({
      envByRepo: {},
      calloutPendingByRepo: {},
      liveModeByRepo: {},
      setViewEnv(repoId, env) {
        // No fixed vocabulary any more (2026-09-07): environments come from
        // the product, and the switcher only ever offers real ones, so the
        // store just records the choice. Empty inputs are still refused.
        if (repoId.length === 0 || env.length === 0) return;
        set((s) => ({ envByRepo: { ...s.envByRepo, [repoId]: env } }));
      },
      markCalloutPending(repoId) {
        if (repoId.length === 0) return;
        set((s) => ({
          calloutPendingByRepo: { ...s.calloutPendingByRepo, [repoId]: true },
        }));
      },
      dismissCallout(repoId) {
        set((s) => {
          if (s.calloutPendingByRepo[repoId] !== true) return s;
          return {
            calloutPendingByRepo: { ...s.calloutPendingByRepo, [repoId]: false },
          };
        });
      },
      setLiveMode(repoId, on) {
        if (repoId.length === 0) return;
        set((s) => {
          if ((s.liveModeByRepo[repoId] === true) === on) return s;
          return { liveModeByRepo: { ...s.liveModeByRepo, [repoId]: on } };
        });
      },
    }),
    {
      name: 'loremap.liveinfra.view',
      // liveModeByRepo is session-only: persisting it would resurrect the
      // elevated 2s cadence on a cold load with no Play control on screen.
      partialize: (s) => ({
        envByRepo: s.envByRepo,
        calloutPendingByRepo: s.calloutPendingByRepo,
      }),
    },
  ),
);

/** The selected view environment for a repo (`prod` until chosen). */
export function useViewEnv(repoId: string): ViewEnvironment {
  return useLiveViewStore((s) => s.envByRepo[repoId] ?? DEFAULT_VIEW_ENVIRONMENT);
}
